import axios from "axios";

const baseUrl = `${process.env.REACT_APP_BASE_URL}/KYCOTP`;

const getCookie = (name) => {
  const cookieArr = document.cookie.split(";");
  for (let cookie of cookieArr) {
    const [key, value] = cookie.split("=");
    if (key.trim() === name) {
      return decodeURIComponent(value);
    }
  }
  return null;
};

const getToken = () => {
  const cookieToken = getCookie("token");
  if (cookieToken) return cookieToken;

  try {
    const adminInfo = JSON.parse(localStorage.getItem("adminInfo") || "{}");
    return adminInfo.token || null;
  } catch {
    return null;
  }
};

const mapStatusFromAPI = (apiStatus) => {
  const normalized = apiStatus?.toLowerCase();
  if (normalized === "approved") return "approved";
  if (normalized === "rejected") return "rejected";
  if (normalized === "expired") return "expired";
  return "pending";
};

const mapStatusToAPI = (componentStatus) => {
  const normalized = componentStatus?.toLowerCase();
  if (normalized === "approved") return "approved";
  if (normalized === "rejected") return "rejected";
  if (normalized === "expired") return "expired";
  return "pending";
};

const normalizeOtpVerificationStatus = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.toLowerCase().trim();
    return normalized === "true" || normalized === "1" || normalized === "verified";
  }
  return false;
};

const getLicenseBucket = (licenseType) => {
  const normalized = (licenseType || "").toLowerCase();
  if (normalized === "photo") return "userImageUrl";
  if (normalized.includes("id_passport")) return "idPassportImageUrl";
  if (normalized === "businesslicense") return "businessLicenceImageUrl";
  if (normalized === "businessregistration") {
    return "businessLicenseRegistrationImageUrl";
  }
  return null;
};

const pickPrimaryStatus = (licenses) => {
  if (!licenses || licenses.length === 0) return "pending";

  let hasPending = false;
  let hasApproved = false;

  for (const license of licenses) {
    const status = license?.status?.toLowerCase();
    if (status === "rejected") return "rejected";
    if (status === "pending") hasPending = true;
    if (status === "approved") hasApproved = true;
  }

  if (hasPending) return "pending";
  if (hasApproved) return "approved";
  return "pending";
};

const normalizeUserId = (userId) =>
  userId ? userId.toString() : null;

const transformKYCWithLicenses = (submission) => {
  const user = submission?.user;
  if (!user) return null;

  const licenses = Array.isArray(submission.licenses)
    ? submission.licenses
    : submission.license
      ? [submission.license]
      : [];

  const imageUrls = {
    userImageUrl: null,
    idPassportImageUrl: null,
    businessLicenceImageUrl: null,
    businessLicenseRegistrationImageUrl: null,
  };
  let rejectionReason = "";

  licenses.forEach((license) => {
    const key = getLicenseBucket(license?.type);
    const fileLink = license?.new_file_link || license?.file_link || null;
    if (key && fileLink) {
      imageUrls[key] = fileLink;
    }
    if (license?.rejection_reason) {
      rejectionReason = license.rejection_reason;
    }
  });

  const status = mapStatusFromAPI(pickPrimaryStatus(licenses));

  return {
    id: normalizeUserId(user.id) || `user-${user.id}`,
    fullName: user.name || "",
    phoneNumber: user.contact_information || "",
    telegramChatId: user.chat_id?.toString() || "",
    companyName: user.business_name || "",
    otpVerificationStatus: user.is_verified || false,
    businessTinNumber: user.tin || "",
    userImageUrl: imageUrls.userImageUrl,
    businessLicenceImageUrl: imageUrls.businessLicenceImageUrl,
    businessLicenseRegistrationImageUrl:
      imageUrls.businessLicenseRegistrationImageUrl,
    idPassportImageUrl: imageUrls.idPassportImageUrl,
    kycVerificationStatus: status,
    rejectionReason,
    userId: user.id,
    expiryDate: submission?.expiry_date || null,
  };
};

const transformKYCUserEntry = (entry) => {
  const userId = entry?.id || entry?.user_id || entry?.userId;
  const kyc = entry?.kyc || {};
  const status =
    mapStatusFromAPI(entry?.kyc_status || kyc?.kyc_status || "pending");

  return {
    id: normalizeUserId(userId) || `user-${userId}`,
    fullName: entry?.name || "",
    phoneNumber: kyc?.phoneNumber || entry?.contact_information || "",
    telegramChatId: (kyc?.chatId ?? entry?.chat_id)?.toString() || "",
    companyName:
      entry?.business_name || kyc?.companyName || kyc?.business_name || "",
    otpVerificationStatus: normalizeOtpVerificationStatus(
      kyc?.otpVerificationStatus ?? entry?.is_verified
    ),
    businessTinNumber: kyc?.tin || entry?.tin || "",
    userImageUrl: kyc?.profilePicture || entry?.profilePicture || null,
    businessLicenceImageUrl:
      kyc?.business_license || entry?.business_license || null,
    businessLicenseRegistrationImageUrl:
      kyc?.business_registration || entry?.business_registration || null,
    idPassportImageUrl:
      kyc?.Identification || kyc?.passport || entry?.Identification || entry?.passport || null,
    kycVerificationStatus: status,
    rejectionReason:
      kyc?.rejection_reason || entry?.kyc_rejection_reason || "",
    userId: userId,
    kycId: kyc?.id || kyc?.kyc_id || entry?.kycID || null,
    kycApproved: entry?.kyc_approved ?? kyc?.is_approved ?? false,
    kycSubmittedAt: entry?.kyc_submitted_at || kyc?.submitted_at || null,
    kycVerifiedAt: entry?.kyc_verified_at || kyc?.verified_at || null,
    kycRejectionReason:
      entry?.kyc_rejection_reason || kyc?.rejection_reason || "",
    hasKyc: entry?.has_kyc ?? false,
    verificationId: kyc?.verificationId || null,
    idType: kyc?.idtype || null,
    expiryDate: kyc?.business_license_expiration_date || entry?.business_license_expiration_date || kyc?.expiry_date || entry?.expiry_date || null,
  };
};

export const transformKYCSubmission = (submission) => {
  if (submission?.user) {
    return transformKYCWithLicenses(submission);
  }

  return transformKYCUserEntry(submission);
};

export const fetchKYCSubmissions = async (
  page = 1,
  limit = 1000,
  status = null
) => {
  const token = getToken();
  if (!token) {
    throw new Error("Authentication token not found");
  }

  try {
    const params = {
      page,
      limit,
    };

    if (status && status !== "All") {
      params.kyc_status = mapStatusToAPI(status);
    }

    const response = await axios.get(`${baseUrl}/admin/getAllUsersWithKYCData`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params,
    });

    const kycSubmissions = response.data?.kycSubmissions || response.data?.users || [];
    const transformed = kycSubmissions
      .filter((entry) => entry?.has_kyc ?? true)
      .map(transformKYCSubmission)
      .filter(Boolean);

    if (status && status !== "All") {
      return transformed.filter((item) => item.kycVerificationStatus === status);
    }

    return transformed;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error("Unauthorized: Please login again");
    }
    if (error.response?.status === 403) {
      throw new Error("Forbidden: Admin access required");
    }
    if (error.response?.status >= 500) {
      throw new Error("Server error: Please try again later");
    }
    if (error.message === "Network Error") {
      throw new Error("Network error: Please check your connection");
    }
    throw error;
  }
};

export const updateKYCStatus = async (
  userId,
  status,
  rejectionReason = "",
  kycId = null,
  expiryDate = null
) => {
  const token = getToken();
  if (!token) {
    throw new Error("Authentication token not found");
  }

  const apiStatus = mapStatusToAPI(status);
  const numericUserId =
    typeof userId === "string" ? parseInt(userId.replace("user-", "")) : userId;

  if (!numericUserId || Number.isNaN(numericUserId)) {
    throw new Error("Invalid user ID");
  }

  try {
    const payload = {
      userId: numericUserId,
      user_id: numericUserId,
      status: apiStatus,
    };

    if (apiStatus === "rejected") {
      payload.rejection_reason = rejectionReason || "Rejected by admin";
    }

    if (apiStatus === "approved" && expiryDate) {
      payload.businessLicenseExpDate = expiryDate;
      payload.business_license_expiration_date = expiryDate;
    }

    if (kycId) {
      payload.kycId = kycId;
      payload.kyc_id = kycId;
    }

    const requestConfig = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    const response = await axios.put(
      `${baseUrl}/updateKYCStatus`,
      payload,
      requestConfig
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error("Unauthorized: Please login again");
    }
    if (error.response?.status === 403) {
      throw new Error("Forbidden: Admin access required");
    }
    if (error.response?.status === 404) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "User not found";
      throw new Error(message);
    }
    if (error.response?.status >= 500) {
      throw new Error("Server error: Please try again later");
    }
    if (error.message === "Network Error") {
      throw new Error("Network error: Please check your connection");
    }
    throw error;
  }
};

export const getKYCSubmissionById = async (id) => {
  const allSubmissions = await fetchKYCSubmissions();
  const submission = allSubmissions.find(
    (sub) => sub.id === id || sub.userId?.toString() === id
  );

  if (!submission) {
    throw new Error("KYC submission not found");
  }

  return submission;
};
